import { CollectionPaths } from '@order-up/common';
import {
  IUser,
  LocationRole,
  TenantRole,
  User,
} from '@order-up/models';
import {
  CreateUserSchema,
  CreateUserType,
  UserSchema,
} from '@order-up/schemas';
import { FirestoreRepository } from '../interfaces/FirestoreRepository';
import { adminApiService } from './adminApi';
import { createApiClient } from './apiClient';
import { createLogger } from '../utils/logger';

const logger = createLogger('UserService');

const usersApiClient = createApiClient('/api/v1/users');

/**
 * Retrieves a user profile document by the user's UID.
 * @param uid The user's Firebase Authentication UID.
 */
const getUserByUid = async (uid: string): Promise<User | null> => {
  logger.debug(`Getting user by UID: ${uid}`);
  const repo = new FirestoreRepository<User>(CollectionPaths.users(), User);
  return repo.getById(uid);
};

/**
 * Profile fields the CALLER contributes when provisioning their own profile.
 * Deliberately tiny — the only things a person types about themselves that the
 * Firebase Auth record does not already carry. Nothing here can influence
 * authorization: `isActive`, `onboardingStatus` and `assignments` are derived
 * server-side, and the route's body schema strips anything else.
 */
export interface ProvisionMyProfileInput {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  phoneNumber?: { countryCode: string; number: string };
}

export interface ProvisionMyProfileResult {
  /** The stored profile — freshly created, or the one that already existed. */
  user: User;
  /** False when a profile was already there and nothing was written. */
  created: boolean;
}

/**
 * Create — or HEAL — the signed-in user's own `users/{uid}` document, on the
 * SERVER.
 *
 * This replaces the browser Firestore write that sign-up used to depend on.
 * That write was the unlinked second half of a two-step sign-up: step 1 created
 * the Firebase Auth account and signed the user in, step 2 wrote `users/{uid}`
 * from the browser, and NOTHING linked them or repaired a failure. Losing step 2
 * — App Check cold start, a permission-denied, a network blip, the user closing
 * the tab — left the account permanently profile-less: the auth app dead-ends on
 * AUTH_PROFILE_UNAVAILABLE forever (its "Try again" can never succeed, because
 * the document will never appear on its own), and the v2 `users/{uid}` trigger
 * that replaced `auth.user().onCreate` never fires either.
 *
 * TWO PROPERTIES CALLERS DEPEND ON:
 *
 *  1. IDEMPOTENT. An existing profile is returned untouched with
 *     `created: false`. Sign-up and heal are the same call.
 *
 *  2. THE SERVER DECIDES WHETHER THE PROFILE EXISTS — not us. A browser cannot
 *     answer that question: under ENFORCED App Check a token-less Firestore read
 *     is NOT rejected with a catchable error, the SDK falls back to the empty
 *     local cache and reports the document as ABSENT. So "I could not read it"
 *     is the most a client may ever claim. Calling this when the profile
 *     actually exists is therefore SAFE and, better, self-correcting: the
 *     response hands back the document the browser could not read.
 *
 * Errors carry a `code` (`PROFILE_PROVISION_BLOCKED` /
 * `PROFILE_PROVISION_FAILED`) via the shared apiClient interceptor, so
 * `translateError` renders real copy rather than the generic fallback.
 */
const provisionMyProfile = async (
  input: ProvisionMyProfileInput = {},
): Promise<ProvisionMyProfileResult> => {
  logger.debug('Provisioning own user profile server-side');

  // The apiClient interceptor unwraps to `response.data`, so this is the
  // Fastify envelope.
  const envelope = (await usersApiClient.post('/me/provision', input)) as unknown as {
    success: boolean;
    data?: { user?: Record<string, unknown>; created?: boolean };
  };

  const raw = envelope?.data?.user;
  if (!raw) {
    throw new Error('PROFILE_PROVISION_FAILED: the server returned no profile.');
  }

  // Rehydrate into a real `User` — the same type `getUserByUid` returns — so a
  // caller can prime the profile query cache with it and every consumer
  // (including the ones that call User's methods) behaves identically to a
  // Firestore read. `fromJSON` revives the ISO date strings on the wire.
  const user = User.fromJSON(raw);
  const created = envelope?.data?.created === true;

  logger.debug('Own user profile provisioned', { uid: user.uid, created });
  return { user, created };
};

/**
 * Creates a new user profile document in Firestore, first validating the input data.
 *
 * ⚠️ ADMIN TOOLING ONLY (`apps/admin`'s UserModal creates a profile for SOMEONE
 * ELSE, which `/users/me/provision` cannot do by design). For the signed-in
 * user's OWN profile use {@link provisionMyProfile} — a browser Firestore write
 * has no server-side repair and is exactly how accounts ended up permanently
 * profile-less.
 *
 * @param userData The raw data for the new user, conforming to the CreateUserType.
 */
const createUserProfile = async (userData: CreateUserType): Promise<void> => {
  logger.debug(`Creating new user profile for UID: ${userData.uid}`);

  const validatedData = CreateUserSchema.parse(userData);

  const userPayload: any = {
    uid: validatedData.uid,
    email: validatedData.email,
    firstName: validatedData.firstName,
    lastName: validatedData.lastName,
    displayName:
      validatedData.displayName ||
      `${validatedData.firstName} ${validatedData.lastName}`,
    emailVerified: validatedData.emailVerified ?? false,
    isActive: validatedData.isActive ?? false,
    assignments: [],
    createdAt: FirestoreRepository.getServerTimestamp(),
    updatedAt: FirestoreRepository.getServerTimestamp(),
  };

  if (validatedData.photoURL !== undefined) {
    userPayload.photoURL = validatedData.photoURL;
  }
  if (validatedData.phoneNumber !== undefined) {
    userPayload.phoneNumber = validatedData.phoneNumber;
  }

  await new FirestoreRepository<User>(
    CollectionPaths.users(),
    User,
  ).createWithId(validatedData.uid, userPayload);
  logger.debug(`Successfully created user profile for UID: ${validatedData.uid}`);
};

/**
 * Updates an existing user's profile document in Firestore.
 * Rules enforce that clients cannot modify the `assignments` array — those
 * are mutated server-side via the API (see grantTenantAssignment / etc.).
 */
const updateUser = async (
  uid: string,
  userData: Partial<Omit<IUser, 'uid' | 'createdAt'>>,
): Promise<void> => {
  logger.debug(`Updating user profile for UID: ${uid}`);

  const validatedData = UserSchema.partial().parse(userData);

  const cleanedData: Record<string, any> = {};
  Object.entries(validatedData).forEach(([key, value]) => {
    // Skip empty assignments arrays (never overwrite server-managed field
    // from the client).
    if (
      value !== undefined &&
      !(key === 'assignments' && Array.isArray(value) && value.length === 0)
    ) {
      cleanedData[key] = value;
    }
  });

  const updatePayload = {
    ...cleanedData,
    updatedAt: FirestoreRepository.getServerTimestamp(),
  };

  await new FirestoreRepository<User>(
    CollectionPaths.users(),
    User,
  ).updateById(uid, updatePayload);
  logger.debug(`Successfully updated user profile for UID: ${uid}`);
};

/**
 * Get-or-create the current user's OWN profile/preferences document.
 *
 * Management users always have a `users/{uid}` doc, so this is a normal update.
 * HQ admins (the separate `admins/{uid}` authz plane) may NOT have one — in
 * that case we seed a VALID `users/{uid}` document from their Firebase Auth
 * identity so the shared account pages behave identically across all apps.
 * Carries NO authorization data: `assignments` stays empty and server-managed.
 *
 * @param uid   The signed-in user's Firebase Auth UID (the doc id).
 * @param userData Self-editable profile/preferences fields.
 * @param seed  Firebase Auth identity used only when creating a fresh doc.
 */
const upsertUserProfile = async (
  uid: string,
  userData: Partial<Omit<IUser, 'uid' | 'createdAt'>>,
  seed: { email: string; emailVerified?: boolean },
): Promise<void> => {
  const existing = await getUserByUid(uid);
  if (existing) {
    await updateUser(uid, userData);
    return;
  }

  logger.debug(`No users/{uid} doc for ${uid} — seeding from Firebase Auth identity`);
  const validatedData = UserSchema.partial().parse(userData);
  const cleanedData: Record<string, any> = {};
  Object.entries(validatedData).forEach(([key, value]) => {
    // Never seed an empty/explicit assignments array — that field is
    // server-managed and seeding it could mis-trigger employee linking.
    if (value !== undefined && key !== 'assignments') {
      cleanedData[key] = value;
    }
  });

  const payload: Record<string, any> = {
    uid,
    email: seed.email,
    emailVerified: seed.emailVerified ?? false,
    isActive: false,
    assignments: [],
    onboardingStatus: 'not_started',
    ...cleanedData,
    createdAt: FirestoreRepository.getServerTimestamp(),
    updatedAt: FirestoreRepository.getServerTimestamp(),
  };

  await new FirestoreRepository<User>(
    CollectionPaths.users(),
    User,
  ).createWithId(uid, payload);
  logger.debug(`Seeded users/{uid} profile doc for ${uid}`);
};

/**
 * Specifically sets the `emailVerified` flag to true for a user.
 */
const setUserEmailVerified = async (uid: string): Promise<void> => {
  logger.debug(`Setting emailVerified to true for UID: ${uid}`);
  await new FirestoreRepository<User>(CollectionPaths.users(), User).updateById(
    uid,
    { emailVerified: true },
  );
};

// ==================== Assignment mutations (API-backed) ====================

/**
 * Grant a tenant-scoped assignment to a user. Calls the admin API which
 * runs server-side UserService.grantTenantAssignment (auto-links Employee
 * records + syncs custom claims). Site-admin-only operation.
 */
const grantTenantAssignment = async (
  uid: string,
  tenantDocId: string,
  role: TenantRole,
): Promise<void> => {
  logger.debug(`Granting tenant assignment: ${uid} → ${tenantDocId} as ${role}`);
  await adminApiService.grantAssignment(uid, {
    scope: 'tenant',
    tenantDocId,
    role,
  });
};

/**
 * Grant a restaurant-scoped assignment to a user.
 */
const grantLocationAssignment = async (
  uid: string,
  tenantDocId: string,
  locationDocId: string,
  role: LocationRole,
): Promise<void> => {
  logger.debug(
    `Granting restaurant assignment: ${uid} → ${tenantDocId}/${locationDocId} as ${role}`,
  );
  await adminApiService.grantAssignment(uid, {
    scope: 'restaurant',
    tenantDocId,
    restaurantDocId: locationDocId,
    role,
  });
};

/**
 * Revoke a tenant-scoped assignment. Optional `successorUid` is
 * promoted to Owner before the revoke if the subject is the sole
 * active Owner.
 */
const revokeTenantAssignment = async (
  uid: string,
  tenantDocId: string,
  reason?: string,
  successorUid?: string,
): Promise<void> => {
  logger.debug(`Revoking tenant assignment: ${uid} → ${tenantDocId}`);
  await adminApiService.revokeAssignment(uid, {
    scope: 'tenant',
    tenantDocId,
    reason,
    successorUid,
  });
};

/**
 * Revoke a restaurant-scoped assignment.
 */
const revokeLocationAssignment = async (
  uid: string,
  tenantDocId: string,
  locationDocId: string,
  reason?: string,
  successorUid?: string,
): Promise<void> => {
  logger.debug(
    `Revoking restaurant assignment: ${uid} → ${tenantDocId}/${locationDocId}`,
  );
  await adminApiService.revokeAssignment(uid, {
    scope: 'restaurant',
    tenantDocId,
    restaurantDocId: locationDocId,
    reason,
    successorUid,
  });
};

/**
 * Suspend a tenant-scoped assignment.
 */
const suspendTenantAssignment = async (
  uid: string,
  tenantDocId: string,
  reason?: string,
): Promise<void> => {
  logger.debug(`Suspending tenant assignment: ${uid} → ${tenantDocId}`);
  await adminApiService.suspendAssignment(uid, {
    scope: 'tenant',
    tenantDocId,
    reason,
  });
};

/**
 * Suspend a restaurant-scoped assignment.
 */
const suspendLocationAssignment = async (
  uid: string,
  tenantDocId: string,
  locationDocId: string,
  reason?: string,
): Promise<void> => {
  logger.debug(
    `Suspending restaurant assignment: ${uid} → ${tenantDocId}/${locationDocId}`,
  );
  await adminApiService.suspendAssignment(uid, {
    scope: 'restaurant',
    tenantDocId,
    restaurantDocId: locationDocId,
    reason,
  });
};

/**
 * Unsuspend (reinstate) a tenant-scoped assignment.
 */
const unsuspendTenantAssignment = async (
  uid: string,
  tenantDocId: string,
): Promise<void> => {
  logger.debug(`Unsuspending tenant assignment: ${uid} → ${tenantDocId}`);
  await adminApiService.unsuspendAssignment(uid, {
    scope: 'tenant',
    tenantDocId,
  });
};

/**
 * Unsuspend (reinstate) a restaurant-scoped assignment.
 */
const unsuspendLocationAssignment = async (
  uid: string,
  tenantDocId: string,
  locationDocId: string,
): Promise<void> => {
  logger.debug(
    `Unsuspending restaurant assignment: ${uid} → ${tenantDocId}/${locationDocId}`,
  );
  await adminApiService.unsuspendAssignment(uid, {
    scope: 'restaurant',
    tenantDocId,
    restaurantDocId: locationDocId,
  });
};

// ==================== Queries / lifecycle ====================

const subscribeToUser = (
  uid: string,
  callback: (user: User | null) => void,
  onError?: (error: Error) => void,
): (() => void) => {
  const repo = new FirestoreRepository<User>(CollectionPaths.users(), User);
  // Server-confirmed only: the invite accept page auto-accepts the moment the
  // profile doc "exists", and the accept API reads the doc server-side — a
  // latency-compensated local snapshot would fire that call before the write
  // commits ("User account not found", seen in prod 2026-08-01).
  //
  // `onError` matters MORE here than on most listeners: this subscription is
  // what rescues the `null` the profile query caches during the sign-up race.
  // A silent death leaves that null served for the whole staleTime.
  return repo.subscribeToDocument(uid, callback, {
    serverConfirmedOnly: true,
    ...(onError ? { onError } : {}),
  });
};

const fetchTenantUsers = async (count: number = 50): Promise<User[]> => {
  const repo = new FirestoreRepository<User>(CollectionPaths.users(), User);
  return repo.find([], [], count);
};

const searchTenantUsers = async (
  searchTerm: string,
  count: number = 20,
): Promise<User[]> => {
  if (!searchTerm) return [];
  const repo = new FirestoreRepository<User>(CollectionPaths.users(), User);
  const results = await repo.search({
    field: 'displayName',
    searchTerm,
    limitNum: count,
  });
  return results.map(({ docId: _docId, ...user }) => user as User);
};

const deactivateUser = async (uid: string): Promise<void> => {
  await new FirestoreRepository<User>(CollectionPaths.users(), User).updateById(
    uid,
    { isActive: false },
  );
};

const reactivateUser = async (uid: string): Promise<void> => {
  await new FirestoreRepository<User>(CollectionPaths.users(), User).updateById(
    uid,
    { isActive: true },
  );
};

const deleteUserDocument = async (uid: string): Promise<void> => {
  const repo = new FirestoreRepository<User>(CollectionPaths.users(), User);
  await repo.deleteById(uid);
};

const deleteUserCompletely = async (uid: string): Promise<void> => {
  await deleteUserDocument(uid);
  logger.warn(
    `Deleted Firestore user document for ${uid}. Firebase Auth user must be deleted via Admin SDK endpoint.`,
  );
};

export const userService = {
  getUserByUid,
  provisionMyProfile,
  createUserProfile,
  updateUser,
  upsertUserProfile,
  setUserEmailVerified,
  grantTenantAssignment,
  grantRestaurantAssignment: grantLocationAssignment,
  revokeTenantAssignment,
  revokeRestaurantAssignment: revokeLocationAssignment,
  suspendTenantAssignment,
  suspendRestaurantAssignment: suspendLocationAssignment,
  unsuspendTenantAssignment,
  unsuspendRestaurantAssignment: unsuspendLocationAssignment,
  subscribeToUser,
  fetchTenantUsers,
  searchTenantUsers,
  deactivateUser,
  reactivateUser,
  deleteUserDocument,
  deleteUserCompletely,
};
